import { HttpBackend, HttpClient, HttpHeaders } from '@angular/common/http';
import { BaseMapListItem, ESRI_DOTS_PER_INCH, ESRI_INCHES_PER_METER, ESRI_VECTOR_LAYER_EXTENT_PROPERTY, FeatureServerDrawingInfo, GetLayerUniqueValueInfo, GISCoordinates, GisServiceList, GISServiceRequest, GISServiceTokenResponse, GISServiceUpdateRequest, GisToken, isLegendFeatureServerDrawingInfo, LayerAttributes, LegendInfoResponse, ServiceTypes } from 'app/shared/models/gis-service-list';
import { gisUserSettings, userSettingLayerDetails } from 'app/shared/models/gis-service-list';
import { GISLayerMapping } from 'app/shared/models/gis-service-list';
import { Config } from './config';
import { Subject, Observable, BehaviorSubject, of, firstValueFrom } from 'rxjs';
import { OLMAPPROJECTION } from 'app/shared/constant';
import { catchError, concatMap, tap } from 'rxjs/operators';
import { AuthService } from './auth.service';
import { Injectable } from '@angular/core';
import { Customer } from '../models/customer';
import { createStyleFunction, readSymbol } from 'ol-esri-style';
import { createFeatureStyle } from 'ol-esri-style/src/styles';
import { Fill, RegularShape, Stroke, Style } from 'ol/style';
import VectorLayer from 'ol/layer/Vector';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import VectorSource from 'ol/source/Vector';
import GeoJSON from 'ol/format/GeoJSON';
import { bbox as bboxStrategy } from 'ol/loadingstrategy';
import EsriJSON from 'ol/format/EsriJSON';
import { TileArcGISRest } from 'ol/source';
import TileLayer from 'ol/layer/Tile';
import LineString from 'ol/geom/LineString';
import * as OlMap from 'ol/Map';
import { Feature } from 'ol';
import { LocationCardService } from '../components/location-card/services/location-card.service';
import { Point } from 'ol/geom';
import { IFeatureServiceDefinition, ILayerDefinition } from '@esri/arcgis-rest-feature-service'
import { Options as OlBaseVectorOptions } from 'ol/layer/BaseVector';
import { Options as OlBaseTileOptions } from 'ol/layer/BaseTile';
import { Options as OlStyleOptions } from 'ol/style/Style';
import * as arcade from '@arcgis/core/arcade.js';
import { ArcGISMapRenderingMetadata, buildArcGISRequestUrl, createArcGISTileSource, normalizeArcGISServiceUrl, shouldUseArcGISViewResolution } from '../utils/arcgis-url.util';
import { resolveArcGISAuthentication } from '../utils/arcgis-auth.util';

const esrijsonFormat = new EsriJSON();

/**
 * #40143 TODO: I believe that we should not extend default types with own attributes.
 * So we did extend those types with name: string in home-map-dashboard.
 * declare module 'ol/layer/BaseVector'
 * When it is used from home-map it does compile but once it's called from somewhere else it does complain about name
 *
 * There was issue when trying to get those defs into one file and import it -
 * Angular work on modules and we wanted to put module declarations inside.
 */
type BaseVectorOptions = OlBaseVectorOptions & { name: string};
type BaseTileOptions = OlBaseTileOptions & { name: string};
type StyleOptions = OlStyleOptions & { name: string};
type GISServiceDefinition = IFeatureServiceDefinition & ArcGISMapRenderingMetadata;


export enum BasemapNames {
    topographic = 'Topographic',
    imagery = 'Imagery (Hybrid)',
    esri = 'ESRI',
    esriImage = 'ESRI Image',
    osm = 'OSM',
    esriBasemap = 'ESRI BaseMap'
}

export interface LayerInfo {
    hasLabels?: boolean;
    type: string,
    minScale: number,
    maxScale: number,
    drawingInfo: FeatureServerDrawingInfo,
    error: string
    extent?: {
        xmin?: number;
        ymin?: number;
        xmax?: number;
        ymax?: number;
    }
}

export interface LayerSettings {
    name: string,
    checked: boolean,
    child: LayerSettings[],
    selection: boolean,
    opacity?: number,
    legendInfo?: LegendInfo[],
    legend?: boolean,
    legendurl?: string,
}

export enum LegendType {
    LINE = 1,
    AREA = 2,
    CIRCLE = 3
}

enum LegendSymbolCode {
    circle = 'esriSMS',
    area = 'esriSFS',
    line = 'esriSLS'
}

export interface LegendInfo {
    label: string;
    url: string | SafeResourceUrl;
    color: string;
    borderColor: string;
    type: LegendType;
    height: string;
}

export interface GISAttributes {
    checkedLayersList: Set<number>,
    layerNameToEsriLayerId: {[index: string]: number},
    olMap: OlMap.default,
    prismLayerList: LayerSettings[],
    esriTileSource: TileArcGISRest,
    esriTileLayer: TileLayer
}



@Injectable()
export class GISService {
    public layerToFeaturesRelation = new Map<string, Map<string, Feature>>();
    public coordinatePickerFromMap = new Subject<GISCoordinates>();

    public gisUserSettingsSubject$: BehaviorSubject<gisUserSettings> = new BehaviorSubject<gisUserSettings>(null);
    private readonly http: HttpClient;

    public locationCardPosition: { top: string; left: string };

    // #46663 Source of pattern images
    private canvas: HTMLCanvasElement;
    private context: CanvasRenderingContext2D;

    /** #46663 Patterns cache, color is a key */
    private patterns: {[key: string]: CanvasPattern} = {};

    private arcGISToken: string;
    private arcgisCustomerId: number = null;
    private cacheCustomerSettings: {
        customerid: number;
        res: Customer;
    }


    constructor(
        backend: HttpBackend,
        private apiHttp: HttpClient,
        private authService: AuthService,
        private _sanitizer:DomSanitizer,
        private locationCardService: LocationCardService
    ) {

        this.authService.gotTheToken$.subscribe((isToken) => {
            if (isToken) {
                this.apiHttp
                    .get<gisUserSettings>(Config.urls.GISServices + '/usersettings')
                    .pipe(
                        catchError(() => of({} as gisUserSettings)),
                    )
                    .subscribe((setting) => {
                        this.gisUserSettingsSubject$.next(setting);
                        if (setting) {
                            this.locationCardPosition = this.locationCardService.getLocationCardPosition(true, setting);
                        } else {
                            this.locationCardPosition = { top: '0px', left: '0px' };
                        }
                    });
            }
        });

        this.http = new HttpClient(backend);

        this.canvas = document.createElement('canvas');
        this.canvas.width = 20;
        this.canvas.height = 20;
        this.context = this.canvas.getContext('2d');
    }

    public gisUserSettingsGet(): Promise<gisUserSettings> {
        return firstValueFrom(this.gisUserSettingsSubject$);
    }

    // GIS Services List
    public getGISServicesList(customerId: number): Observable<GisServiceList[]> {
        return this.apiHttp.get<GisServiceList[]>(Config.urls.GISServices + '/services/' + customerId);
    }

    public getServiceToken(
        url: string,
        urlAuth: string | null | undefined,
        username: string,
        pass: string,
    ): Observable<GISServiceTokenResponse> {
        const plan = resolveArcGISAuthentication(url, urlAuth);
        switch (plan.kind) {
            case 'portal-token':
                return this.getPortalToken(plan.tokenUrl, username, pass);
            case 'token-exchange':
                return this.getTwoFactorToken(url, plan.tokenUrl, username, pass);
            case 'server-token':
                return this.getBasicToken(url, username, pass);
        }
    }

    private getPortalToken(urlAuth: string, username: string, pass: string): Observable<GISServiceTokenResponse> {
        const data =
            'username=' + username + '&password=' + pass + '&client=referer&referer=' + encodeURIComponent(window.location.origin) + '&expiration=60&f=json';
        return this.http.post<GISServiceTokenResponse>(urlAuth, data, {
            headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }),
        });
    }

    // Implementation for #21978 - using "two factor" tokens
    public getTwoFactorToken(
        url: string,
        urlAuth: string,
        username: string,
        pass: string,
    ): Observable<GISServiceTokenResponse> {
        return this.getPortalToken(urlAuth, username, pass).pipe(
            concatMap((res) => {
                const secondTokenURL = `${urlAuth}?request=getToken&serverUrl=${encodeURIComponent(url)}&token=${
                    res.token
                }&referer=www.arcgis.com&f=json`;
                return this.http.get<GISServiceTokenResponse>(secondTokenURL);
            }),
        );
    }

    /** Default and standard, generateToken request */
    public getBasicToken(url: string, username: string, pass: string) {
        const data =
            'username=' + username + '&password=' + pass + '&client=referer&referer=' + encodeURIComponent(window.location.origin) + '&expiration=60&f=json';
        const urls = url.split('/');
        const newurl = urls[0] + '//' + urls[2] + '/' + urls[3] + '/tokens/generateToken';
        return this.http.post<GISServiceTokenResponse>(newurl, data, {
            headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }),
        });
    }

    public postArcGISLayerList(url: string, token: string): Observable<GISServiceDefinition> {
        const data = new URLSearchParams({ token }).toString();
        const requestUrl = buildArcGISRequestUrl(url, '', { f: 'pjson', token: null });
        return this.http.post<GISServiceDefinition>(requestUrl, data, {
            headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }),
        });
    }
    public getArcGISLayerList(url: string): Observable<GISServiceDefinition> {
        return this.http.get<GISServiceDefinition>(buildArcGISRequestUrl(url, '', { f: 'pjson' }), {
            headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }),
        });
    }
    public getFeatureLayerData(url: string) {
        return this.http.get<LayerInfo>(url, {
            headers: new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded' }),
        });
    }

    private getConfiguredServiceUrl(service: GisServiceList): string {
        return service.serviceType === ServiceTypes.MAPS || service.serviceType === ServiceTypes.FEATURE
            ? normalizeArcGISServiceUrl(service.url)
            : service.url;
    }

    // GIS Services Update
    public updateGISService(gisService: GisServiceList) {
        const body: GISServiceUpdateRequest = {
            serviceId: gisService.serviceId,
            serviceType: gisService.serviceType,
            URL: this.getConfiguredServiceUrl(gisService),
            appId: gisService.appId,
            authUrl: gisService.authURL,
            AuthDetails: '',
            CustomerId: gisService.customerId,
            AuthMethod: gisService.authMethod,
            UserName: gisService.userName,
            Password: gisService.password,
            authParams: gisService.authParams,
        };
        return this.apiHttp.post<void>(Config.urls.GISServices + '/services/' + gisService.customerId, body);
    }

    // GIS Services Save
    public addGISService(gisService: GisServiceList) {
        const body: GISServiceRequest = {
            serviceType: gisService.serviceType,
            URL: this.getConfiguredServiceUrl(gisService),
            appId: gisService.appId,
            authUrl: gisService.authURL,
            AuthDetails: '',
            CustomerId: gisService.customerId,
            AuthMethod: gisService.authMethod,
            UserName: gisService.userName,
            Password: gisService.password,
            authParams: gisService.authParams,
        };
        return this.apiHttp.post<void>(Config.urls.GISServices + '/services/' + gisService.customerId, body);
    }

    // GIS Services Delete
    public deleteGISservice(customerId: number, serviceId: number) {
        return this.apiHttp.delete<{isError?: boolean}>(Config.urls.GISServices + '/services/' + customerId + '/' + serviceId);
    }

    //BaseMap
    public getGISBasemap() {
        return this.apiHttp.get<BaseMapListItem[]>(Config.urls.GISServices + '/basemaps');
    }

    public getGisToken() {
        return this.apiHttp.get<GisToken>(Config.urls.gisToken);
    }

    public updateGisUserSettings(gisUserSettings: gisUserSettings) {
        gisUserSettings.customerSettings = gisUserSettings
            ? gisUserSettings.customerSettings.filter((x) => x.cid !== 0)
            : null;
        if (gisUserSettings && gisUserSettings._etag) {
            delete gisUserSettings._etag;
        } // Can't be on update call

        this.gisUserSettingsSubject$.next(gisUserSettings);

        return this.apiHttp.post<void>(Config.urls.GISServices + '/usersettings', gisUserSettings);
    }

    public getCustomerSettings(customerid: number, reloadCache = true) {
        if(!reloadCache
            && this.cacheCustomerSettings
            && this.cacheCustomerSettings.customerid === customerid
        ) {
            return of(this.cacheCustomerSettings.res)
        } else {
            return this.apiHttp.get<Customer>(Config.getUrl(Config.urls.activateCustomer) + '/' + customerid)
                .pipe(
                    tap((res) => {
                        if(res) {
                            this.cacheCustomerSettings = {
                                customerid: customerid,
                                res: res
                            }
                        }
                    })
                )
        }
    }

    public getGisLayerMapping(customerId: number): Observable<GISLayerMapping[]> {
        return this.apiHttp.get<GISLayerMapping[]>(Config.urls.GISServices + '/layermappings/' + customerId);
    }

    public async generateArcGisToken(service: GisServiceList) {
        // if there are multiple services, we need to generate a token for each service
        // so i am resetting the token to undefined
        this.arcGISToken = undefined;
        this.arcGISToken = await this.generateToken(service) ?? undefined;
    }

    public clearArcGisToken() {
        this.arcGISToken = undefined;
    }

    public setArcGisCustomerId(customerId: number): void {
        this.arcgisCustomerId = customerId;
        if (customerId !== null && customerId !== undefined) {
            sessionStorage.setItem('ARCGIS_CUSTOMER_ID', customerId.toString());
        } else {
            sessionStorage.removeItem('ARCGIS_CUSTOMER_ID');
        }
    }

    public getArcGisCustomerId(): number | null {
        if (this.arcgisCustomerId !== null) {
            return this.arcgisCustomerId;
        }

        const stored = sessionStorage.getItem('ARCGIS_CUSTOMER_ID');
        if (stored) {
            this.arcgisCustomerId = Number(stored);
            return this.arcgisCustomerId;
        }

        return null;
    }

    public clearArcGisCustomerId(): void {
        this.arcgisCustomerId = null;
        sessionStorage.removeItem('ARCGIS_CUSTOMER_ID');
    }

    private async generateToken(service: GisServiceList) {
        if (!service.userName && !service.password) {
            return null;
        }
        const tokenRes = await firstValueFrom(this
            .getServiceToken(this.getConfiguredServiceUrl(service), service.authURL, service.userName, service.password)
        );

        return tokenRes?.token ?? null;
    }

    public getSavedLayerSettings(customerID: number, gisUserSetting: gisUserSettings, layerName: string): userSettingLayerDetails {
        const affectedCustomer = gisUserSetting.customerSettings.find((x) => x.cid === customerID);
        if (!affectedCustomer) {
            return undefined;
        }
        const affectedLayer = affectedCustomer.layers.find((x) => x.name === layerName);
        return affectedLayer;
    }


    public async layerLoad(
        services: GisServiceList[],
        gisUserSetting: gisUserSettings,
        customerID: number,
        shouldProcess: () => boolean,
        attributes: GISAttributes,
        options: {
            defaultOpacity: number
        }
    ) {
        if (services !== null) {
            // #42845 making sure we process map server and then feature server so that the layers dont cover each other.
            const mapServices = services.filter(service => service.serviceType === "maps");
            const featServices = services.filter(service => service.serviceType === "feat");
            services = [...mapServices, ...featServices];
            for (const service of services) {

                if(!shouldProcess()) return;

                const serviceType = ServiceTypes.FEATURE as ServiceTypes;
                if (service.serviceType == ServiceTypes.WMS || service.serviceType == ServiceTypes.WFS) {
                    console.error(`PRISM does not handle service type ${service.serviceType}`);
                } else if (serviceType == ServiceTypes.MAPS || serviceType == ServiceTypes.FEATURE) {
                    await this.generateArcGisToken(service);
                    await this.arcLayerLoading(
                        service,
                        gisUserSetting,
                        customerID,
                        shouldProcess,
                        {
                            checkedLayersList: attributes.checkedLayersList,
                            layerNameToEsriLayerId: attributes.layerNameToEsriLayerId,
                            olMap: attributes.olMap,
                            prismLayerList: attributes.prismLayerList,
                            esriTileSource: attributes.esriTileSource,
                            esriTileLayer: attributes.esriTileLayer
                        },
                        {
                            defaultOpacity: options.defaultOpacity
                        }
                    );

                }
            }
        }
    }

    private createLegendInfo(label: string, url: string | SafeResourceUrl, symbol, color?: string, height?: string, borderColor?: string): LegendInfo {
        let legendType: LegendType;

        if (symbol && symbol.type === LegendSymbolCode.circle) {
            legendType = LegendType.CIRCLE;
        } else if (symbol && symbol.type === LegendSymbolCode.line) {
            legendType = LegendType.LINE;
        } else {
            legendType = LegendType.AREA;
        }

        return {
            label: label,
            url: url,
            color: color ? color : null,
            borderColor: borderColor ? borderColor : null,
            height: height ? height: null,
            type: legendType
        }
    }

    public async arcLayerLoading(
        service: GisServiceList,
        gisUserSetting: gisUserSettings,
        customerID: number,
        shouldProcess: () => boolean,
        attributes: GISAttributes,
        options: {
            defaultOpacity: number
        }
    ) {
        const serviceUrl = this.getConfiguredServiceUrl(service);
        this.layerToFeaturesRelation.clear();
        const serviceToken = this.arcGISToken;
        const response = serviceToken
            ? await firstValueFrom(this.postArcGISLayerList(serviceUrl, serviceToken))
            : await firstValueFrom(this.getArcGISLayerList(serviceUrl));

        const arcLayers = response;
        let hasLabels = false;


        // To make vector layers almost invisible. If they are completely invisible feature selection tools does not work properly
        const olStyleNoFill = new Fill({color: 'rgba(255,255,255,0.05)'});
        const olStyleNoStroke = new Stroke({color: 'rgba(255,255,255,0.05)'});
        const olStyleNoImage = new RegularShape({
            fill: olStyleNoFill,
            stroke: olStyleNoStroke,
            points: 4,
            radius: 10,
            angle: Math.PI / 4,
        });

        const layerAttributes: {[key: string]: LayerAttributes} = {};
        if (service.serviceType === ServiceTypes.MAPS) {
            const legendUrl = buildArcGISRequestUrl(serviceUrl, 'legend', { f: 'json', token: serviceToken });
            const legendInfo = await fetch(legendUrl);
            const legendJson: LegendInfoResponse = await legendInfo.json();
            if (legendJson && legendJson.layers) {
                for (const item of legendJson.layers) {
                    layerAttributes[item.layerId] = item;
                }
            }
        }

        if (arcLayers.layers) {
            // make vector layers to allow feature selection tool
            for (const lyr of arcLayers.layers) {

                if(!shouldProcess()) return;

                // #23256
                const sourceUrl = buildArcGISRequestUrl(serviceUrl, String(lyr.id));
                const layerUrl = buildArcGISRequestUrl(sourceUrl, '', { f: 'json', token: serviceToken });
                const layerInfo = await firstValueFrom(this.getFeatureLayerData(layerUrl));
                hasLabels = hasLabels || (!layerInfo.error && layerInfo.hasLabels === true);

                if (service.serviceType === ServiceTypes.FEATURE) {
                    layerAttributes[lyr.id] = {
                        layerType: layerInfo.type,
                        minScale: layerInfo.minScale ? layerInfo.minScale : Number.MAX_SAFE_INTEGER,
                        maxScale: layerInfo.maxScale,
                        legend: layerInfo.drawingInfo,
                    };
                }


                const legend = layerAttributes[lyr.id]?.legend;
                let layerVisible = true;
                const savedLayerSettings = this.getSavedLayerSettings(customerID, gisUserSetting, lyr.name);
                const layerSettingsToUse: LayerSettings = {
                    name: lyr.name,
                    checked: true,
                    selection: true,
                    child: [],
                    opacity: isLegendFeatureServerDrawingInfo(legend) && legend.transparency? (legend.transparency / 100) : options.defaultOpacity,
                    legendInfo: [],
                };

                if (savedLayerSettings) {
                    if (savedLayerSettings.isVisible !== undefined) {
                        layerSettingsToUse.checked = savedLayerSettings.isVisible;
                        layerVisible = savedLayerSettings.isVisible;
                    }
                    if (savedLayerSettings.isSelectable !== undefined) {
                        layerSettingsToUse.selection = savedLayerSettings.isSelectable;
                    }
                }

                if (layerSettingsToUse.checked) {
                    attributes.checkedLayersList.add(lyr.id);
                }

                attributes.layerNameToEsriLayerId[lyr.name] = lyr.id;

                if (layerAttributes[lyr.id] && layerAttributes[lyr.id].layerType === 'Feature Layer') {
                    const currentLegend = legend;
                    const vectorSource = new VectorSource({
                        format: new GeoJSON(),
                        loader: async (extent, resolution, projection) => {
                            const url = buildArcGISRequestUrl(sourceUrl, 'query/', {
                                f: 'json',
                                returnGeometry: true,
                                spatialRel: 'esriSpatialRelIntersects',
                                geometry: JSON.stringify({
                                    xmin: extent[0],
                                    ymin: extent[1],
                                    xmax: extent[2],
                                    ymax: extent[3],
                                    spatialReference: { wkid: 102100 },
                                }),
                                geometryType: 'esriGeometryEnvelope',
                                inSR: 102100,
                                outFields: '*',
                                outSR: 102100,
                                token: serviceToken,
                            });
                            const layerData = await this.getFeatureLayerData(url).toPromise();
                            const features = esrijsonFormat.readFeatures(layerData, {
                                featureProjection: projection,
                            });

                            this.setLayerToFeaturesRelation(lyr, features);

                            if (features.length > 0) {
                                if (isLegendFeatureServerDrawingInfo(currentLegend) &&
                                    !currentLegend.renderer.field1 &&
                                    currentLegend.renderer.valueExpression) {
                                    for (const feature of features) {
                                        const result = await this.evaluateArcadeExpression(currentLegend.renderer.valueExpression, feature);
                                        if (result !== null) {
                                            feature.set('__arcadeValue', result);
                                        }
                                    }
                                }
                                vectorSource.addFeatures(features);
                            }
                        },
                        strategy: bboxStrategy,
                    });

                    let minRes = layerAttributes[lyr.id].maxScale / (ESRI_INCHES_PER_METER * ESRI_DOTS_PER_INCH);
                    let maxRes = layerAttributes[lyr.id].minScale / (ESRI_INCHES_PER_METER * ESRI_DOTS_PER_INCH);

                    if (minRes === maxRes) {
                        if (minRes === 0) {
                            // Both are 0, show at all resolutions
                            maxRes = undefined;
                        } else {
                            // Equal but not at 0, show only at/below that resolution
                            minRes = 0;
                        }
                    }

                    const options: BaseVectorOptions = {
                        name: lyr.name,
                        visible: layerVisible,
                        source: vectorSource,
                        minResolution: minRes,
                        maxResolution: maxRes,
                        opacity: isLegendFeatureServerDrawingInfo(legend) && legend.transparency ? (legend.transparency / 100) : 1,
                        zIndex: 1
                    };
                    const newVectorLayer = new VectorLayer(options);



                    if(isLegendFeatureServerDrawingInfo(legend) && legend.renderer?.uniqueValueInfos) {
                        const styleFunction = await this.handleUniqueValueInfos(attributes, legend);
                        newVectorLayer.setStyle(styleFunction);
                    } else if(isLegendFeatureServerDrawingInfo(legend) && legend.renderer?.symbol?.marker && legend.renderer.symbol.marker.style === 'arrow') {
                        const styleFunction = this.arrowStyleFunction(attributes, legend);
                        newVectorLayer.setStyle(styleFunction);
                    } else if(isLegendFeatureServerDrawingInfo(legend) && ['esriSFSBackwardDiagonal', 'esriSFSForwardDiagonal'].includes(legend.renderer?.symbol?.style)) {
                        const styleFunction = this.diagonalStyleFunction(legend);
                        newVectorLayer.setStyle(styleFunction);
                    } else if (service.serviceType === ServiceTypes.FEATURE && layerInfo && !layerInfo.error) {
                        const styleFunction = await createStyleFunction(layerInfo);
                        newVectorLayer.setStyle(styleFunction);
                    } else if (service.serviceType === ServiceTypes.MAPS) {
                        const style = new Style({
                            fill: olStyleNoFill,
                            stroke: olStyleNoStroke,
                            image: olStyleNoImage,
                        });
                        newVectorLayer.setStyle(style);
                    }

                    let legendUrl: SafeResourceUrl = null;
                    let legendColor: string = null;
                    let legendOutlineColor: string = null;

                    if(!isLegendFeatureServerDrawingInfo(legend)) {
                         legend.forEach((legendEntry) => {
                            legendUrl = this._sanitizer.bypassSecurityTrustResourceUrl(
                                'data:image/png;base64, ' + legendEntry.imageData,
                            );
                            layerSettingsToUse.legendInfo.push(
                                this.createLegendInfo(legendEntry.label, legendUrl, legendEntry.symbol, null, null)
                            );
                        })
                    } else if (isLegendFeatureServerDrawingInfo(legend) && legend.renderer.symbol) {
                        const symbol = legend.renderer.symbol;
                        if (symbol && symbol.imageData) {
                            legendUrl = this._sanitizer.bypassSecurityTrustResourceUrl(
                                'data:image/png;base64, ' + symbol.imageData,
                            );
                        } else if (symbol && symbol.color) {
                            legendColor = 'rgba(' + symbol.color.toString() + ')';
                            if(symbol.outline) {
                                legendOutlineColor = 'rgba(' + symbol.outline.color.toString() + ')';
                            }
                        }

                        let height = '100%';
                        if (symbol && symbol.height) {
                            height = symbol.height + 'px';
                        }
                        layerSettingsToUse.legendInfo.push(
                            this.createLegendInfo(legend.label, legendUrl, symbol, legendColor, height, legendOutlineColor)
                        );

                    } else if (isLegendFeatureServerDrawingInfo(legend) && legend.renderer.uniqueValueInfos) {
                        const symbols = legend.renderer.uniqueValueInfos;
                        symbols.forEach((syms) => {
                            const sym = syms.symbol;
                            let color: string = null;
                            let outlineColor: string = null;
                            let legendUrl: SafeResourceUrl = null;
                            if (sym.imageData) {
                                legendUrl = this._sanitizer.bypassSecurityTrustResourceUrl(
                                    'data:image/png;base64, ' + sym.imageData,
                                );
                            } else if (sym.color) {
                                color = 'rgba(' + sym.color.toString() + ')';
                                if(sym.outline) {
                                    outlineColor = 'rgba(' + sym.outline.color.toString() + ')';
                                }
                            }

                            layerSettingsToUse.legendInfo.push(
                                this.createLegendInfo(syms.label, legendUrl, sym, color, null, outlineColor)
                            );
                        });
                    }

                    this.setExtentOnLayer(newVectorLayer, sourceUrl, serviceToken);
                    attributes.olMap.addLayer(newVectorLayer);
                    attributes.prismLayerList.push(layerSettingsToUse);
                }
            }

            if (service.serviceType === ServiceTypes.MAPS) {
                const filteredLayersList = Array.from(attributes.checkedLayersList).filter(function (el) {
                    return el !== null && el !== undefined;
                });

                attributes.esriTileSource = createArcGISTileSource(serviceUrl, {
                    projection: OLMAPPROJECTION,
                    LAYERS: 'show:' + filteredLayersList.toString(),
                    token: serviceToken,
                }, shouldUseArcGISViewResolution(arcLayers, hasLabels) ? attributes.olMap : undefined, arcLayers);

                const esriTLOptions: BaseTileOptions = {
                    source: attributes.esriTileSource,
                    name: 'esriTileLayer',
                    visible: filteredLayersList.length > 0,
                };
                attributes.esriTileLayer = new TileLayer(esriTLOptions);

                attributes.olMap.addLayer(attributes.esriTileLayer);
            }
        }
    }

    private async evaluateArcadeExpression(expression: string, feature: Feature): Promise<string | null> {
        try {
            const profile = arcade.createArcadeProfile('visualization');
            const executor = await arcade.createArcadeExecutor(expression, profile);

            const attributes: {[key: string]: string | number | boolean | null} = {};
            const properties = feature.getProperties();
            for (const key in properties) {
                if (key !== 'geometry') {
                    attributes[key] = properties[key];
                }
            }

            const arcadeFeature = {
                attributes: attributes,
                geometry: feature.getGeometry()
            };

            const result = await executor.executeAsync({
                $feature: arcadeFeature
            });
            return result !== null && result !== undefined ? String(result) : null;
        } catch (error) {
            console.error('Arcade evaluation error:', error);
            return null;
        }
    }

    private async handleUniqueValueInfos(attributes: GISAttributes, legend: FeatureServerDrawingInfo) {
        const self = this;
        const useExpression = !legend.renderer.field1 && legend.renderer.valueExpression;
        const fieldName = legend.renderer.field1;

        const assocLegend: {[key: string]: GetLayerUniqueValueInfo} = {};

        for(const uniqueValueInfos of legend.renderer.uniqueValueInfos) {
            const legendType = uniqueValueInfos.value;
            assocLegend[legendType] = uniqueValueInfos;
        }

        // #37072 Need to compile style uniqueValueInfos
        // #37072 Returned function cannot be async and we need to await for image
        const compiledStyles: {[key: string]: Style} = {};

        for(const uniqueValueInfos of legend.renderer.uniqueValueInfos) {
            const legendType = uniqueValueInfos.value;
            const symbol = uniqueValueInfos.symbol;
            const styleDef: StyleOptions = readSymbol(symbol);
            const compiledStyle: Style = await createFeatureStyle(styleDef);
            compiledStyles[legendType] = compiledStyle;
        }

        let defaultStyle: Style | null = null;
        if (legend.renderer.defaultSymbol) {
            const defaultStyleDef: StyleOptions = readSymbol(legend.renderer.defaultSymbol);
            defaultStyle = await createFeatureStyle(defaultStyleDef);
        }

        return function (feature: Feature<Point>) {
            const featureType: string = useExpression ? feature.get('__arcadeValue') : feature.get(fieldName);
            const featureStyleDef = assocLegend[featureType];

            if(featureStyleDef?.symbol?.marker && featureStyleDef.symbol.marker.style === 'arrow') {
                const color = 'rgba(' + featureStyleDef.symbol.color.toString() + ')';
                return self.featureArrowFn(feature, attributes, color);
            } else {
                const style = compiledStyles[featureType];

                // #48105 Return empty style if legend not found
                if(!style) {
                    if (defaultStyle) {
                        return [defaultStyle];
                    }
                    return [new Style()]
                }

                return [style];
            }
        }
    }


    /** #46663 Diagonal fill areas */
    private diagonalStyleFunction(legend: FeatureServerDrawingInfo) {
        const symbol = legend.renderer.symbol;
        const color = 'rgba(' + symbol.color.toString() + ')';

        if(!this.patterns[color]) {
            const transparentColor = 'rgba(255, 255, 255, 0)';

            this.context.fillStyle = transparentColor;
            this.context.fillRect(0, 0, this.canvas.width, this.canvas.height);
            this.context.strokeStyle = color;
            this.context.moveTo(this.canvas.width, 0);
            this.context.lineTo(0, this.canvas.height);
            this.context.stroke();

            const pcanvas = document.createElement('canvas');
            const pctx = pcanvas.getContext('2d');
            this.patterns[color] = pctx.createPattern(this.canvas, 'repeat');
        }

        const pattern = this.patterns[color];

        return function () {
            const stroke = new Stroke({
                color: color,
                width: symbol.outline.width
            });

            const fillShape = new Style({
                fill: new Fill({color: pattern}),
                stroke: stroke
            });

            return [fillShape];
        };
    }

    private featureArrowFn(feature: Feature<Point>, attributes: GISAttributes, color: string) {
        let MAP_MAX_SCALE_PLUS_ONE = 20;
        const mapScale = attributes.olMap.getView().getZoom();

        if (mapScale + 1 > MAP_MAX_SCALE_PLUS_ONE) MAP_MAX_SCALE_PLUS_ONE = mapScale + 1;

        let scale = (MAP_MAX_SCALE_PLUS_ONE - mapScale);
        if (scale > 3) scale = scale * ((MAP_MAX_SCALE_PLUS_ONE - mapScale) / 3);

        const strokeArrowWidth = 3;
        const strokeWidth = 2;

        const arrowWings = 2 * scale;
        const arrowMultiplier = 1 + 2 * (scale / MAP_MAX_SCALE_PLUS_ONE);
        const arrowDivider = 2 - (scale / MAP_MAX_SCALE_PLUS_ONE);

        const geometry = feature.getGeometry();
        const styles = [
            new Style({
                stroke: new Stroke({
                    color: color,
                    width: strokeWidth
                })
            })
        ];

        const coords = geometry.getCoordinates();

        if(coords.length < 2) return styles;

        const start = coords[coords.length - 2];
        const end = coords[coords.length - 1];

        // We don't want to do math root to compute distance because of performance, use something simiular
        const dx = end[0] - start[0];
        const dy = end[1] - start[1];
        const rotation = Math.atan2(dy, dx);

        let d = Math.abs(dx) + Math.abs(dy);
        if(d > 15) d = d / 10; else d = 2;

        const sx = end[0] - dx / d;
        const sy = end[1] - dy / d;

        const p = [sx, sy];

        const lineStr1 = new LineString([p, [p[0] - arrowWings * arrowMultiplier, p[1] + arrowWings / arrowDivider]]);
        lineStr1.rotate(rotation, p);
        const lineStr2 = new LineString([p, [p[0] - arrowWings * arrowMultiplier, p[1] - arrowWings / arrowDivider]]);
        lineStr2.rotate(rotation, p);

        const stroke = new Stroke({
            color: color,
            width: strokeArrowWidth
        });

        styles.push(new Style({
            geometry: lineStr1,
            stroke: stroke
        }));
        styles.push(new Style({
            geometry: lineStr2,
            stroke: stroke
        }));

        return styles;
    }


    private arrowStyleFunction(attributes: GISAttributes, legend: FeatureServerDrawingInfo) {
        const symbol = legend.renderer.symbol;
        const color = 'rgba(' + symbol.color.toString() + ')';
        const self = this;
        return function (feature: Feature<Point>) {
            return self.featureArrowFn(feature, attributes, color)
        };
    }

    private setLayerToFeaturesRelation(layer: ILayerDefinition, features: Feature[]) {
        const featuresMap = features.reduce((acc, curr) => {
            const name = this.getFeatureNameProp(curr);
            if (!name) return acc;

            acc.set(name, curr);

            return acc;
        }, new Map<string, Feature>());

        this.layerToFeaturesRelation.set(layer.name, featuresMap);
    }

    public getFeatureNameProp(feat: Feature): string {
        const featProperties = feat.getProperties();
        const featureKeys = Object.keys(featProperties);
        const nameKeysHierarchy = ['name', 'assetid', 'facilityid', 'gis_id', 'unitid'];

        let nameKey: string;
        for(let i = 0; i<nameKeysHierarchy.length; i++) {
            const current = featureKeys.find(v => v.toLowerCase() === nameKeysHierarchy[i]);

            if (current) {
                nameKey = current;
                break;
            }
        }
        if (!nameKey) {
            nameKey = featureKeys[0] === 'geometry' ? featureKeys[1] : featureKeys[0];
        }

        if(typeof featProperties[nameKey] !== 'string') {
            return `${featProperties[nameKey]}`;
        } else {
            return featProperties[nameKey];
        }
    }

    async setExtentOnLayer(layer: VectorLayer, sourceUrl: string, token?: string): Promise<void> {
        const url = buildArcGISRequestUrl(sourceUrl, 'query', {
            where: '1=1',
            returnCountOnly: true,
            returnExtentOnly: true,
            f: 'json',
            outSR: 102100,
            token,
        });

        const extentData = await firstValueFrom(this.getFeatureLayerData(url));
        if (extentData.extent) {
            const extent = [
                extentData.extent.xmin,
                extentData.extent.ymin,
                extentData.extent.xmax,
                extentData.extent.ymax,
            ];
            layer.set(ESRI_VECTOR_LAYER_EXTENT_PROPERTY, extent);
        }
    }
}
